import React, { useEffect, useState } from "react";
import { TrophyFilled } from "@ant-design/icons";
import { MTN_YELLOW } from "../../constants/Colors";
import ScoreSubmitCard from "./kente/ScoreSubmitCard";

/**
 * The end of a run, in two beats.
 *
 * The first beat is the result and nothing else: the outcome on a big face,
 * the score, the best, and the line the game has for you. The leaderboard ask
 * waits (Ken, 2026-09-04) — landing a signup form on someone the instant
 * their last life goes is the worst moment to ask for anything, and it reads
 * as the game selling to them rather than talking to them.
 *
 * The face follows the OUTCOME rather than always being sad. We praise a good
 * run now, and a sad face over "Chale, you dey try!" would be incoherent.
 *
 * Shared by all three games because all three had the same block, and the
 * beat needs to feel identical wherever you are.
 */

/** How long the result sits on its own before the ask appears. Long enough to
 *  register, short enough not to feel like a loading screen. */
const CTA_DELAY_MS = 1600;

export interface GameOverPanelProps {
  score: number;
  best: number;
  /** The taunt or the praise — see components/games/encouragement. */
  message: string;
  /** True when the run earned praise; it decides the face. */
  wentWell: boolean;
  /** The game's own brag: bands remembered, days survived, rounds cleared. */
  peak: { value: number; singular: string; plural: string; verb: string };
  /** Slug the score posts under, and the rounds figure that bounds it. */
  gameSlug: string;
  rounds: number;
  onPlayAgain: () => void;
}

export default function GameOverPanel({
  score,
  best,
  message,
  wentWell,
  peak,
  gameSlug,
  rounds,
  onPlayAgain,
}: GameOverPanelProps) {
  const [askVisible, setAskVisible] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setAskVisible(true), CTA_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const beatenBest = score >= best && score > 0;

  return (
    <div className="mt-3 rounded-2xl bg-neutral-900 px-4 py-5 ring-1 ring-white/10">
      {/* Beat one: what happened. */}
      <div className="flex flex-col items-center text-center">
        <span className="text-4xl leading-none" aria-hidden>
          {wentWell ? "🎉" : "😩"}
        </span>
        <p className="m-0 mt-2 text-[11px] uppercase tracking-wide text-white/40">
          {wentWell ? "Nice run" : "Womp womp"}
        </p>

        <p className="m-0 mt-2 text-5xl font-bold leading-none tabular-nums">
          {score}
        </p>
        <p className="m-0 mt-1 text-xs text-white/50">
          {beatenBest ? "Your best yet" : `Best ${best}`}
        </p>
        <p className="m-0 mt-1 text-xs text-white/45">
          {peak.value} {peak.value === 1 ? peak.singular : peak.plural}{" "}
          {peak.verb}
        </p>
        <p className="m-0 mt-3 max-w-xs text-sm leading-snug text-white/85">
          {message}
        </p>
      </div>

      {/* Beat two: the ask, once the result has landed. */}
      <div
        className={[
          "transition-opacity duration-500",
          askVisible ? "opacity-100" : "pointer-events-none opacity-0",
        ].join(" ")}
        aria-hidden={!askVisible}
      >
        {score > 0 &&
          (showSubmit ? (
            <div className="mt-5">
              <ScoreSubmitCard
                game={gameSlug}
                score={score}
                bestSpan={peak.value}
                rounds={rounds}
              />
            </div>
          ) : (
            <div className="mt-5 rounded-xl bg-white/5 p-3">
              <p className="m-0 flex items-center gap-1.5 text-sm font-semibold">
                <TrophyFilled style={{ color: MTN_YELLOW }} />
                Put {score} on the leaderboard
              </p>
              <p className="m-0 mt-1 text-xs leading-relaxed text-white/60">
                See how you rank this week, and let your people try to beat it.
              </p>
              <button
                type="button"
                onClick={() => setShowSubmit(true)}
                className="mt-3 w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:brightness-95"
                style={{ backgroundColor: MTN_YELLOW }}
              >
                Add my score
              </button>
            </div>
          ))}

        <button
          type="button"
          onClick={onPlayAgain}
          className="mt-3 w-full rounded-lg border border-white/20 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/10"
        >
          Play again
        </button>
      </div>
    </div>
  );
}
